import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Wallet, Calculator, Lock, Unlock, CheckCircle2, Settings2, Download, ShieldAlert, Eye, CalendarClock, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
// Local (IST) month — toISOString() is UTC and would still show last month until 05:30 IST on the 1st.
const thisMonth = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })();
const fmtDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
const istDateOf = (ts) => (ts ? new Date(ts).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "");
const nextMonthFirst = (m) => { const [y, mo] = m.split("-").map(Number); return mo === 12 ? `${y + 1}-01-01` : `${y}-${String(mo + 1).padStart(2, "0")}-01`; };

function StatCard({ label, value, tone }) {
  return (
    <Card className="border-slate-200 shadow-none p-4">
      <p className="overline text-slate-400 mb-1">{label}</p>
      <p className={`text-2xl font-serif-display ${tone || "text-navy"}`}>{value}</p>
    </Card>
  );
}

function CalcRow({ label, value }) {
  return <div className="flex justify-between py-1 text-sm"><span className="text-slate-500">{label}</span><span className="text-navy font-medium">{value}</span></div>;
}

// One-off lines for an employee in a payroll month (IntenCiv, Nov 2026). Earnings lines (arrears,
// bonus, incentive, reimbursement, other) are PAID with this month's salary; tick PF / ESI only
// when the amount is part of wages for that contribution. Recovery / Advance are deducted.
const ADJ_KINDS = ["Arrears", "Bonus", "Incentive", "Reimbursement", "Other", "Recovery", "Advance"];
const ADJ_DEDUCTIONS = ["Recovery", "Advance"];
const ADJ_DEFAULT_WAGES = { Arrears: { pf: true, esi: true }, Incentive: { pf: false, esi: true } };
const emptyAdj = (kind = "Arrears") => ({ kind, amount: "", remarks: "", ...(ADJ_DEFAULT_WAGES[kind] || { pf: false, esi: false }) });

function PayrollAdjustments({ month, employeeId, locked }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyAdj());
  const { data: all } = useQuery({
    queryKey: ["payrollAdjustments", month],
    queryFn: async () => (await api.get(`/payroll/${month}/adjustments`)).data,
  });
  const rows = (all || []).filter((a) => a.employee_id === employeeId);
  const isDeduction = ADJ_DEDUCTIONS.includes(form.kind);
  const done = (msg) => {
    toast.success(msg);
    qc.invalidateQueries({ queryKey: ["payrollAdjustments", month] });
  };
  const add = useMutation({
    mutationFn: async () => (await api.post("/payroll/adjustments", {
      employee_id: employeeId, month, kind: form.kind, amount: parseFloat(form.amount), remarks: form.remarks,
      affects: isDeduction ? ["net"] : ["net", "ctc", ...(form.pf ? ["pf"] : []), ...(form.esi ? ["esi"] : [])],
    })).data,
    onSuccess: () => { setForm(emptyAdj(form.kind)); done("Adjustment saved. Click Recalculate to apply it."); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/payroll/adjustments/${id}`)).data,
    onSuccess: () => done("Adjustment removed. Click Recalculate to apply it."),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const ready = !locked && parseFloat(form.amount) > 0;

  return (
    <Card className="p-4 border-slate-200 shadow-none space-y-3" data-testid="payroll-adjustments">
      <div>
        <p className="overline text-slate-400">Adjustments this month</p>
        <p className="text-xs text-slate-500 mt-1">Arrears, bonus, incentive etc. are paid with this month&apos;s salary. Tick PF / ESI only if the amount counts as wages for it (check with your CA if unsure). Recovery / Advance are deducted.</p>
      </div>
      {rows.length > 0 && (
        <div className="space-y-1">
          {rows.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-sm border-b border-slate-100 py-1">
              <span className="text-slate-700">
                {a.kind}{a.remarks ? <span className="text-slate-400"> — {a.remarks}</span> : null}
                {(a.affects || []).includes("pf") && <span className="ml-2 text-[10px] text-teal-700">PF</span>}
                {(a.affects || []).includes("esi") && <span className="ml-1 text-[10px] text-teal-700">ESI</span>}
              </span>
              <span className="flex items-center gap-2">
                <span className={ADJ_DEDUCTIONS.includes(a.kind) ? "text-red-600" : "text-navy"}>
                  {ADJ_DEDUCTIONS.includes(a.kind) ? "− " : "+ "}{inr(a.amount)}
                </span>
                {!locked && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500" disabled={remove.isPending}
                    onClick={() => remove.mutate(a.id)} data-testid={`adj-delete-${a.id}`}>Remove</Button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {locked ? (
        <p className="text-xs text-slate-400">This month is locked — unlock it to change adjustments.</p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Select value={form.kind} onValueChange={(k) => setForm({ ...emptyAdj(k), amount: form.amount, remarks: form.remarks })}>
              <SelectTrigger data-testid="adj-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{ADJ_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" min="1" placeholder="Amount (₹)" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="adj-amount" />
            <Button variant="outline" disabled={!ready || add.isPending} onClick={() => add.mutate()} data-testid="adj-add">Add</Button>
          </div>
          <Input placeholder="Remarks (e.g. September arrears)" value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="adj-remarks" />
          {!isDeduction && (
            <div className="flex gap-4 text-xs text-slate-600">
              <label className="flex items-center gap-1"><input type="checkbox" checked={form.pf}
                onChange={(e) => setForm({ ...form, pf: e.target.checked })} data-testid="adj-pf" /> Count for PF</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={form.esi}
                onChange={(e) => setForm({ ...form, esi: e.target.checked })} data-testid="adj-esi" /> Count for ESI</label>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function Payroll() {
  const { isSuperAdmin } = useAuth();
  const qc = useQueryClient();
  const [month, setMonth] = useState(thisMonth);
  const [detailId, setDetailId] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [newConfig, setNewConfig] = useState({
    effective_date: "", pf_ceiling: 25000, pf_employee_rate: 0.12, pf_employer_rate: 0.12,
    esi_ceiling: 21000, esi_employee_rate: 0.0075, esi_employer_rate: 0.0325,
    gratuity_rate_days: 15, gratuity_rate_period_days: 26, proration_basis: "calendar_day",
  });
  const [overrideField, setOverrideField] = useState("");
  const [overrideValue, setOverrideValue] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["payrollTable", month],
    queryFn: async () => (await api.get(`/payroll/${month}`)).data,
  });
  const header = data?.header;
  const rows = data?.rows || [];
  const status = header?.status; // undefined -> never calculated | Calculated | Locked | Approved
  const monthInProgress = !!data && data.month_ended === false;
  const needsRecalc = !!data?.needs_recalc_after_month_end;
  const asOf = header?.as_of || istDateOf(header?.calculated_at);
  const lockBlockedReason = monthInProgress
    ? `Month in progress — payroll can be locked from ${fmtDate(nextMonthFirst(month))}`
    : needsRecalc ? "Calculated before the month ended — Recalculate first" : "";

  const { data: detail } = useQuery({
    queryKey: ["payrollDetail", month, detailId],
    queryFn: async () => (await api.get(`/payroll/${month}/${detailId}`)).data,
    enabled: !!detailId,
  });

  const { data: configHistory } = useQuery({
    queryKey: ["statutoryConfig"],
    queryFn: async () => (await api.get("/payroll/statutory-config")).data,
    enabled: configOpen,
  });

  const calculate = useMutation({
    mutationFn: async () => (await api.post(`/payroll/${month}/calculate`)).data,
    onSuccess: (d) => { toast.success(`Calculated for ${d.employee_count} employee(s)${d.error_count ? ` — ${d.error_count} error(s) to resolve` : ""}`); qc.invalidateQueries({ queryKey: ["payrollTable", month] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const lock = useMutation({
    mutationFn: async () => (await api.post(`/payroll/${month}/lock`, {})).data,
    onSuccess: () => { toast.success("Payroll locked"); qc.invalidateQueries({ queryKey: ["payrollTable", month] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const unlock = useMutation({
    mutationFn: async (reason) => (await api.post(`/payroll/${month}/unlock`, { reason })).data,
    onSuccess: () => { toast.success("Payroll unlocked"); qc.invalidateQueries({ queryKey: ["payrollTable", month] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const approve = useMutation({
    mutationFn: async () => (await api.post(`/payroll/${month}/approve`, {})).data,
    onSuccess: () => { toast.success("Payroll approved"); qc.invalidateQueries({ queryKey: ["payrollTable", month] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const addConfig = useMutation({
    mutationFn: async () => (await api.post("/payroll/statutory-config", newConfig)).data,
    onSuccess: () => { toast.success("New statutory configuration saved"); qc.invalidateQueries({ queryKey: ["statutoryConfig"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const override = useMutation({
    mutationFn: async () => (await api.post(`/payroll/${month}/${detailId}/override`, {
      field: overrideField, new_value: Number(overrideValue), reason: overrideReason,
    })).data,
    onSuccess: () => {
      toast.success("Override applied");
      qc.invalidateQueries({ queryKey: ["payrollDetail", month, detailId] });
      qc.invalidateQueries({ queryKey: ["payrollTable", month] });
      setOverrideField(""); setOverrideValue(""); setOverrideReason("");
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const doUnlock = () => {
    const reason = window.prompt("Reason for unlocking this payroll month? (required — it will be logged)", "");
    if (reason) unlock.mutate(reason);
  };

  const downloadPayslip = async (employeeId, code) => {
    try {
      const res = await api.get(`/payroll/${month}/${employeeId}/payslip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Payslip_${code}_${month}.pdf`; a.click();
    } catch { toast.error("Payslip not available — payroll must be Locked or Approved first"); }
  };

  return (
    <div>
      <PageHeader overline="Payroll" title="Monthly Payroll" icon={Wallet}
        subtitle="Attendance → Earned Salary → PF/ESI/Gratuity → Net Salary → Employer Cost — calculated, reviewed, and locked before payslips go out."
        actions={<>
          <Input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setDetailId(null); }} className="w-40" data-testid="payroll-month-input" />
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => setConfigOpen(true)} data-testid="statutory-config-btn">
              <Settings2 className="h-4 w-4 mr-1" /> Statutory Configuration
            </Button>
          )}
        </>}
      />

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {status && <StatusBadge status={status} />}
        {(!status || status === "Calculated") && (
          <Button size="sm" className="bg-navy hover:bg-navy-700" disabled={calculate.isPending} onClick={() => calculate.mutate()} data-testid="calculate-btn">
            <Calculator className="h-4 w-4 mr-1" /> {status ? "Recalculate" : "Calculate Payroll"}
          </Button>
        )}
        {status === "Calculated" && isSuperAdmin && (
          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700"
            disabled={lock.isPending || (header?.error_count || 0) > 0 || !!lockBlockedReason} title={lockBlockedReason || undefined}
            onClick={() => lock.mutate()} data-testid="lock-btn">
            <Lock className="h-4 w-4 mr-1" /> Lock Payroll
          </Button>
        )}
        {status === "Locked" && (
          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" disabled={approve.isPending} onClick={() => approve.mutate()} data-testid="approve-btn">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Approve Payroll
          </Button>
        )}
        {(status === "Locked" || status === "Approved") && isSuperAdmin && (
          <Button size="sm" variant="outline" className="border-amber-300 text-amber-700" disabled={unlock.isPending} onClick={doUnlock} data-testid="unlock-btn">
            <Unlock className="h-4 w-4 mr-1" /> Unlock
          </Button>
        )}
      </div>

      {header && monthInProgress && status === "Calculated" && (
        <div className="flex items-start gap-3 mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="payroll-preview-banner">
          <CalendarClock className="h-5 w-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Month in progress — figures as of {fmtDate(asOf)}, preview only.</p>
            <p className="text-amber-700">Paid days count only days up to that date; the rest of the month is not counted yet.
              Payroll can be locked from {fmtDate(nextMonthFirst(month))}, after a final Recalculate.</p>
          </div>
        </div>
      )}
      {header && needsRecalc && status === "Calculated" && (
        <div className="flex items-start gap-3 mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="payroll-recalc-banner">
          <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
          <p><span className="font-semibold">These figures are from {fmtDate(asOf)}, before the month ended.</span> Recalculate to get the final payroll before locking.</p>
        </div>
      )}
      {header && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <StatCard label="Employees" value={header.employee_count} />
          <StatCard label="Gross Payroll" value={inr(header.totals?.gross)} />
          <StatCard label="Net Salary" value={inr(header.totals?.net_salary)} />
          <StatCard label="Employer Cost" value={inr(header.totals?.employer_cost)} />
          <StatCard label="Errors" value={header.error_count || 0} tone={header.error_count ? "text-red-600" : "text-navy"} />
          <StatCard label="Warnings" value={header.warning_count || 0} tone={header.warning_count ? "text-amber-600" : "text-navy"} />
        </div>
      )}

      {isLoading ? <Loading /> : rows.length === 0 ? (
        <EmptyState icon={Wallet} title="No payroll calculated for this month yet"
          hint="Set each employee's salary structure (Employee → Salary tab), then Calculate Payroll for this month."
          action={<Button className="bg-navy" disabled={calculate.isPending} onClick={() => calculate.mutate()}><Calculator className="h-4 w-4 mr-1" /> Calculate Payroll</Button>} />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Employee</TableHead>
              <TableHead className="overline text-slate-500">Paid Days</TableHead>
              <TableHead className="overline text-slate-500">Basic</TableHead>
              <TableHead className="overline text-slate-500">HRA</TableHead>
              <TableHead className="overline text-slate-500">Travel</TableHead>
              <TableHead className="overline text-slate-500">Gross</TableHead>
              <TableHead className="overline text-slate-500">Emp. PF</TableHead>
              <TableHead className="overline text-slate-500">Emp. ESI</TableHead>
              <TableHead className="overline text-slate-500">Net Salary</TableHead>
              <TableHead className="overline text-slate-500">Employer Cost</TableHead>
              <TableHead className="overline text-slate-500">Flags</TableHead>
              <TableHead className="text-right overline text-slate-500">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.employee_id} className="table-row-i" data-testid={`payroll-row-${r.employee_id}`}>
                  <TableCell className="font-medium text-navy">
                    {r.employee_name}<span className="text-slate-400 text-xs font-mono-i ml-2">{r.employee_code}</span>
                    {r.structure?.structure_type === "Consolidated" && (
                      <span className="block text-[10px] uppercase tracking-wide text-teal mt-0.5">Consolidated fee</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-600">{r.structure?.structure_type === "Consolidated" ? "—" : `${r.attendance?.paid_days} / ${r.attendance?.proration_days}`}</TableCell>
                  <TableCell className="text-slate-600">{r.structure?.structure_type === "Consolidated" ? inr(r.earned?.gross) : inr(r.earned?.basic)}</TableCell>
                  <TableCell className="text-slate-600">{r.structure?.structure_type === "Consolidated" ? "—" : inr(r.earned?.hra)}</TableCell>
                  <TableCell className="text-slate-600">{r.structure?.structure_type === "Consolidated" ? "—" : inr(r.earned?.travelling)}</TableCell>
                  <TableCell className="text-navy font-medium">{inr(r.earned?.gross)}</TableCell>
                  <TableCell className="text-slate-600">{inr(r.pf?.employee_pf)}</TableCell>
                  <TableCell className="text-slate-600">{inr(r.esi?.employee_esi)}</TableCell>
                  <TableCell className="text-navy font-semibold">{inr(r.net_salary)}</TableCell>
                  <TableCell className="text-slate-700">{inr(r.employer_cost)}</TableCell>
                  <TableCell>
                    {r.errors?.length > 0 ? <span className="text-xs text-red-600 font-medium">{r.errors.length} error(s)</span>
                      : r.warnings?.length > 0 ? <span className="text-xs text-amber-600 font-medium">{r.warnings.length} warning(s)</span>
                      : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setDetailId(r.employee_id)} data-testid={`view-${r.employee_id}`}><Eye className="h-4 w-4" /></Button>
                    {(status === "Locked" || status === "Approved") && (
                      <Button size="sm" variant="ghost" onClick={() => downloadPayslip(r.employee_id, r.employee_code)} data-testid={`payslip-${r.employee_id}`}><Download className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Employee calculation detail */}
      <Dialog open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="payroll-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">{detail.employee_name} — {month}</DialogTitle></DialogHeader>

              {(detail.errors?.length > 0 || detail.warnings?.length > 0) && (
                <div className="space-y-1">
                  {detail.errors?.map((e, i) => <p key={i} className="text-sm text-red-600 flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5 shrink-0" /> {e}</p>)}
                  {detail.warnings?.map((w, i) => <p key={i} className="text-sm text-amber-600 flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5 shrink-0" /> {w}</p>)}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="p-4 border-slate-200 shadow-none">
                  <p className="overline text-slate-400 mb-2">Attendance</p>
                  <CalcRow label="Calendar Days" value={detail.attendance.calendar_days} />
                  <CalcRow label="Present" value={detail.attendance.present_days} />
                  <CalcRow label="Half Days" value={detail.attendance.half_days} />
                  <CalcRow label="Weekly Offs" value={detail.attendance.weekly_offs} />
                  <CalcRow label="Holidays" value={detail.attendance.holidays} />
                  <CalcRow label="Paid Leave" value={detail.attendance.paid_leave_days} />
                  <CalcRow label="LWP" value={detail.attendance.lwp_days} />
                  <CalcRow label="Unmarked Absence (LOP)" value={detail.attendance.unmarked_absence_days} />
                  {detail.attendance.upcoming_days > 0 && (
                    <CalcRow label="Not yet counted (rest of month)" value={detail.attendance.upcoming_days} />
                  )}
                  <CalcRow label="Paid Days" value={`${detail.attendance.paid_days} / ${detail.attendance.proration_days}`} />
                </Card>
                <Card className="p-4 border-slate-200 shadow-none">
                  <p className="overline text-slate-400 mb-2">Earned Salary</p>
                  {detail.structure?.structure_type === "Consolidated" ? (
                    <>
                      <CalcRow label="Consolidated Fee" value={inr(detail.earned.gross)} />
                      <CalcRow label="TDS %" value={`${detail.structure?.tds_percent || 0}%`} />
                      <CalcRow label="Earned Gross" value={inr(detail.earned.gross)} />
                    </>
                  ) : (
                    <>
                      <CalcRow label="Basic" value={inr(detail.earned.basic)} />
                      <CalcRow label="HRA" value={inr(detail.earned.hra)} />
                      <CalcRow label="Travelling" value={inr(detail.earned.travelling)} />
                      <CalcRow label="Earned Gross" value={inr(detail.earned.gross)} />
                    </>
                  )}
                  {detail.adjustment_earnings > 0 && (
                    <>
                      <CalcRow label="Adjustments (arrears, bonus…)" value={inr(detail.adjustment_earnings)} />
                      <CalcRow label="Gross Pay" value={inr(detail.gross_pay)} />
                    </>
                  )}
                  {detail.comp_off_payout?.amount > 0 && (
                    <CalcRow label={`Comp-off payout (${detail.comp_off_payout.unused_days} day, double wage)`} value={inr(detail.comp_off_payout.amount)} />
                  )}
                  <p className="overline text-slate-400 mt-3 mb-2">PF (on Basic)</p>
                  <CalcRow label="PF Wage" value={inr(detail.pf.pf_wage)} />
                  <CalcRow label="Employee PF" value={inr(detail.pf.employee_pf)} />
                  <CalcRow label="Employer PF (EPF + EPS)" value={`${inr(detail.pf.employer_epf)} + ${inr(detail.pf.employer_eps)}`} />
                  <p className="overline text-slate-400 mt-3 mb-2">ESI</p>
                  <CalcRow label="ESI Wage (Gross: Basic + HRA + TA)" value={inr(detail.esi.esi_wage)} />
                  <CalcRow label="Coverage" value={detail.esi.applicable ? (detail.esi.coverage ? "Yes" : "No") : "Not Applicable"} />
                  <CalcRow label="Employee ESI" value={inr(detail.esi.employee_esi)} />
                  <CalcRow label="Employer ESI" value={inr(detail.esi.employer_esi)} />
                  <p className="overline text-slate-400 mt-3 mb-2">Gratuity (employer cost only)</p>
                  <CalcRow label="Monthly Provision" value={inr(detail.gratuity.monthly_provision)} />
                </Card>
              </div>

              <Card className="p-4 border-navy/10 bg-navy/[0.03] shadow-none">
                <p className="overline text-slate-400 mb-2">View Calculation</p>
                {detail.calculation_breakdown?.map((line, i) => <p key={i} className="text-xs text-slate-600 font-mono-i leading-relaxed">{line}</p>)}
              </Card>

              <div className="flex justify-between items-center pt-1">
                <span className="text-sm text-slate-500">Net Salary</span>
                <span className="text-xl font-serif-display text-navy">{inr(detail.net_salary)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Employer Cost / CTC</span>
                <span className="text-lg font-serif-display text-slate-700">{inr(detail.employer_cost)}</span>
              </div>

              <PayrollAdjustments month={month} employeeId={detail.employee_id}
                locked={status === "Locked" || status === "Approved"} />

              {isSuperAdmin && (
                <Card className="p-4 border-slate-200 shadow-none space-y-2">
                  <p className="overline text-slate-400">Manual Override (Super Admin — logged with a reason)</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="field e.g. tds" value={overrideField} onChange={(e) => setOverrideField(e.target.value)} />
                    <Input type="number" placeholder="new value" value={overrideValue} onChange={(e) => setOverrideValue(e.target.value)} />
                    <Button variant="outline" disabled={override.isPending || !overrideField || !overrideValue || overrideReason.trim().length < 3}
                      onClick={() => override.mutate()} data-testid="apply-override-btn">Apply</Button>
                  </div>
                  <Input placeholder="Reason (required)" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
                  {detail.overridden_fields?.length > 0 && (
                    <p className="text-xs text-amber-600">Overridden fields: {detail.overridden_fields.join(", ")}</p>
                  )}
                </Card>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Statutory configuration */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="bg-white max-w-xl max-h-[85vh] overflow-y-auto" data-testid="statutory-config-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Statutory Configuration</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400 -mt-2">Adding a row never edits an existing one — a month already Locked keeps using the rates that were in force when it was calculated.</p>

          {(configHistory || []).length > 0 && (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Effective</TableHead>
                <TableHead className="overline text-slate-500">PF Ceiling</TableHead>
                <TableHead className="overline text-slate-500">ESI Ceiling</TableHead>
                <TableHead className="overline text-slate-500">Proration</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {configHistory.map((c, i) => (
                  <TableRow key={i}><TableCell>{c.effective_date}</TableCell><TableCell>{inr(c.pf_ceiling)}</TableCell><TableCell>{inr(c.esi_ceiling)}</TableCell><TableCell>{c.proration_basis}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-3">
            <div><Label>New Effective Date</Label><Input type="date" value={newConfig.effective_date} onChange={(e) => setNewConfig({ ...newConfig, effective_date: e.target.value })} className="mt-1" /></div>
            <div><Label>PF Ceiling</Label><Input type="number" value={newConfig.pf_ceiling} onChange={(e) => setNewConfig({ ...newConfig, pf_ceiling: Number(e.target.value) })} className="mt-1" /></div>
            <div><Label>PF Employee %</Label><Input type="number" step="0.01" value={newConfig.pf_employee_rate} onChange={(e) => setNewConfig({ ...newConfig, pf_employee_rate: Number(e.target.value) })} className="mt-1" /></div>
            <div><Label>PF Employer %</Label><Input type="number" step="0.01" value={newConfig.pf_employer_rate} onChange={(e) => setNewConfig({ ...newConfig, pf_employer_rate: Number(e.target.value) })} className="mt-1" /></div>
            <div><Label>ESI Ceiling</Label><Input type="number" value={newConfig.esi_ceiling} onChange={(e) => setNewConfig({ ...newConfig, esi_ceiling: Number(e.target.value) })} className="mt-1" /></div>
            <div><Label>ESI Employee %</Label><Input type="number" step="0.0001" value={newConfig.esi_employee_rate} onChange={(e) => setNewConfig({ ...newConfig, esi_employee_rate: Number(e.target.value) })} className="mt-1" /></div>
            <div><Label>ESI Employer %</Label><Input type="number" step="0.0001" value={newConfig.esi_employer_rate} onChange={(e) => setNewConfig({ ...newConfig, esi_employer_rate: Number(e.target.value) })} className="mt-1" /></div>
            <div><Label>Gratuity Rate (days/period)</Label><div className="flex gap-1 mt-1"><Input type="number" value={newConfig.gratuity_rate_days} onChange={(e) => setNewConfig({ ...newConfig, gratuity_rate_days: Number(e.target.value) })} /><Input type="number" value={newConfig.gratuity_rate_period_days} onChange={(e) => setNewConfig({ ...newConfig, gratuity_rate_period_days: Number(e.target.value) })} /></div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>Close</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={addConfig.isPending || !newConfig.effective_date} onClick={() => addConfig.mutate()} data-testid="save-statutory-config">Save New Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
